import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type AssistantCitationJSON = z.infer<typeof AssistantCitationSchema.AssistantCitationJSONZ>;
export type AssistantCitationParams = z.infer<typeof AssistantCitationSchema.AssistantCitationZ>;

export default class AssistantCitationSchema {
  public static readonly AssistantCitationJSONZ = z.object({
    fileId: z.uuid(),
    fileName: z.string(),
    page: z.number().nullish(),
    nodeId: z.uuid().nullish(),
    score: z.number().nullish(),
    semanticScore: z.number().nullish(),
    combinedScore: z.number().nullish(),
    matched: z.boolean().nullish(),
    text: z.string().nullish(),
  });

  public static readonly AssistantCitationZ = z.object({
    fileId: z.instanceof(Guid),
    fileName: z.string(),
    page: z.number().optional(),
    nodeId: z.instanceof(Guid).optional(),
    score: z.number().optional(),
    semanticScore: z.number().optional(),
    combinedScore: z.number().optional(),
    matched: z.boolean().optional(),
    text: z.string().optional(),
  });

  public static validate(values: unknown): AssistantCitationParams {
    return parseOrThrow(AssistantCitationSchema.AssistantCitationZ, values, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): AssistantCitationJSON {
    return parseOrThrow(AssistantCitationSchema.AssistantCitationJSONZ, json, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is AssistantCitationParams {
    try {
      AssistantCitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is AssistantCitationJSON {
    try {
      AssistantCitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: AssistantCitationJSON): AssistantCitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(AssistantCitationSchema.AssistantCitationJSONZ, json, {
      logError: true,
      schemaName: "AssistantCitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return AssistantCitationSchema.codec.decode(parsed);
  }

  public static serialize(legalEntity: AssistantCitationParams): AssistantCitationJSON {
    if (isNullOrUndefined(legalEntity)) return legalEntity;
    const parsed = AssistantCitationSchema.validate(legalEntity);
    if (isNullOrUndefined(parsed)) return parsed;
    return AssistantCitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    AssistantCitationSchema.AssistantCitationJSONZ,
    AssistantCitationSchema.AssistantCitationZ,
    {
      decode: (data) => ({
        fileId: Guid.fromJSON(data.fileId) ?? Guid.EMPTY,
        fileName: data.fileName,
        page: data.page ?? undefined,
        nodeId: Guid.fromJSON(data.nodeId) ?? undefined,
        score: data.score ?? undefined,
        semanticScore: data.semanticScore ?? undefined,
        combinedScore: data.combinedScore ?? undefined,
        matched: data.matched ?? undefined,
        text: data.text ?? undefined,
      }),
      encode: (citation) => ({
        fileId: citation.fileId.toJSON(),
        fileName: citation.fileName,
        page: citation.page,
        nodeId: citation.nodeId ? citation.nodeId.toJSON() : undefined,
        score: citation.score,
        semanticScore: citation.semanticScore,
        combinedScore: citation.combinedScore,
        matched: citation.matched,
        text: citation.text,
      }),
    }
  );
}
